/**
 * @copyright 2023 Honda Motor Co., Ltd. All rights reserved.
 */

import { FC, useState, useEffect } from "react";
import { Box, Dialog, DialogContent, Typography } from "@mui/material";
import WarningRoundedIcon from "@mui/icons-material/WarningRounded";
import { useTranslation } from "react-i18next";

interface Props {
  onMount?: (isPortrait: boolean) => void;
  onPortrait?: () => void;
  onLandscape?: () => void;
}

/**
 * Screen orientation component
 *
 * @param {Object} Props Properties
 * @return {JSX.Element} Component
 */
const ScreenOrientation: FC<Props> = ({ onMount, onPortrait, onLandscape }) => {
  const { screen } = window;
  const isPortrait: () => boolean = () => {
    // if orientation.type supported
    if (screen.orientation.type) {
      return (
        screen.orientation.type === "portrait-primary" ||
        screen.orientation.type === "portrait-secondary"
      );
    }

    // else: determine by orientation.angle
    return screen.orientation.angle === 0 || screen.orientation.angle === 180;
  };
  useEffect(() => {
    // Register event handler
    screen.orientation.onchange = (ev: Event) => {
      if (isPortrait()) {
        if (onPortrait) {
          onPortrait();
        }
      } else {
        if (onLandscape) {
          onLandscape();
        }
      }
    };

    // Do callback on mount
    if (onMount) {
      onMount(isPortrait());
    }

    return () => {
      screen.orientation.onchange = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return <></>;
};

/**
 * Component of warning screen when device orientation is portrait mode.
 * @return {JSX.Element} Component
 */
export const OrientationWarningDialog = () => {
  const [open, setOpen] = useState(false);
  const { t } = useTranslation();
  return (
    <>
      <ScreenOrientation
        onMount={(isPortrait) => setOpen(isPortrait)}
        onPortrait={() => setOpen(true)}
        onLandscape={() => setOpen(false)}
      />
      <Dialog
        open={open}
        fullScreen
        sx={{
          zIndex: 9999,
          textAlign: "center",
        }}
      >
        <Box
          sx={{
            display: "flex",
            height: "100%",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <DialogContent>
            <WarningRoundedIcon color="warning" style={{ fontSize: 150 }} />
            <Typography variant="h2">{t("AWVUI-MSG150")}</Typography>
          </DialogContent>
        </Box>
      </Dialog>
    </>
  );
};
